import { Component, HostListener, TemplateRef, ViewChild } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter, Subscription } from "rxjs";
import { SplashScreenService } from "./shared/splash-screen/splash-screen.service";
import { Title } from "@angular/platform-browser";
import { AppointmentToastrService } from "./shared/appointment-toastr/appointment-toastr.service";
import { UserRoleService } from "./shared/user-role/user-role.service";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../environments/environment";
import { FeatureAccessService } from "./shared/service/feature-access.service";
import { SessionOutService } from "./session-out/session-out.service";
import { MatDialog } from "@angular/material/dialog";
import { AuthService } from "./core/auth/auth.service";
import { WebsocketService } from "./shared/service/websocket.service";
import { CommonComponentService } from "./shared/common-component/common-component.service";
import { CommonComponentComponent } from "./shared/common-component/common-component.component";
import { DomainType, ShareService } from "./shared/service/share.service";
import introJs from "intro.js";
import { DashboardService } from "./dashboard/dashboard.service";
import { LoaderService } from "./shared/loader/loader.service";
import { ChatbotComponent } from "./shared/component/chatbot/chatbot.component";
import { StorageService } from "./Service/storage.service";
import { SafeArea } from 'capacitor-plugin-safe-area';
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";

@Component({
  selector: "body[kt-root]",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false
})
export class AppComponent {
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  // ── Global Barcode Scanner ──────────────────────────────────────────────────
  private barcodeBuffer: string = "";
  private lastBarcodeKeyTime: number = 0;

  @HostListener('document:keydown', ['$event'])
  handleGlobalBarcodeKeydown(event: KeyboardEvent) {
    // Ignore input from text fields so normal typing is not affected
    const target = event.target as HTMLElement;
    if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) {
      return;
    }
    const currentTime = Date.now();
    // Barcode scanners send chars very rapidly; reset buffer if gap > 150 ms
    if (currentTime - this.lastBarcodeKeyTime > 150) {
      this.barcodeBuffer = "";
    }
    this.lastBarcodeKeyTime = currentTime;

    if (event.key === 'Enter') {
      const term = this.barcodeBuffer.trim();
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (guidRegex.test(term)) {
        this.barcodeBuffer = "";
        this.router.navigate([`/Patients/view/${term}`]);
        return;
      }
      this.barcodeBuffer = "";
    } else if (event.key.length === 1) {
      this.barcodeBuffer += event.key;
    }
  }
  // ───────────────────────────────────────────────────────────────────────────
  loader: boolean;
  isDoctorOrAdmin: boolean = false;
  IsAIChatEnable = 'false';
  domain = window.location.host;
  showChatbot: boolean = false;
  showWarningPopup = false;
  @ViewChild("sessionOutDialog") sessionOutDialog: TemplateRef<any>;
  templateRftoShare: any;
  intro: any;
  /**
   * Component constructor
   *
   * @param router: Router
   * @param layoutConfigService: LayoutCongifService
   * @param splashScreenService: SplashScreenService
   */
  constructor(
    private translate: TranslateService,
    private router: Router,
    private splashScreenService: SplashScreenService,
    private titleService: Title,
    private toastrService: AppointmentToastrService,
    private userRoleService: UserRoleService,
    private featureService: FeatureAccessService,
    private sessionTimer: SessionOutService,
    public dialog: MatDialog,
    private readonly authService: AuthService,
    public wsService: WebsocketService,
    public commonComponentService: CommonComponentService,
    public shareService: ShareService,
    public dashboardService: DashboardService,
    public loaderService: LoaderService,
    private readonly storageService: StorageService,
    private swUpdate: SwUpdate
  ) {
    if (swUpdate.isEnabled) {
      swUpdate.versionUpdates
        .pipe(
          filter(
            (event): event is VersionReadyEvent =>
              event.type === 'VERSION_READY'
          )
        )
        .subscribe(() => {
          window.location.reload();
        });
    }
    this.translate.addLangs(environment?.languages);
    this.featureService.loadFromStorage();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event?.urlAfterRedirects?.replace("/", "") && this.storageService.getItem("IsAdmin") === "true") {
          this.checkAndOpenTermsDialog();
        }
      });
  }

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  async ngOnInit() {
    // Initialize intro.js
    this.intro = introJs();

    // Get initial safe area
    const { insets } = await SafeArea.getSafeAreaInsets();
    this.applyInsets(insets);

    // Listen for changes
    SafeArea.addListener('safeAreaChanged', ({ insets }) => {
      this.applyInsets(insets);
    });
    if (this.shareService.domainName == "") {
      this.checkDomain();
    }
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const currentRoute = event.urlAfterRedirects;
        this.showChatbot = !(
          currentRoute == "/" ||
          currentRoute == "/ForgetPasswordEmailVerification" ||
          currentRoute?.startsWith("/viewer")
        );
        if (!this.showChatbot) {
          sessionStorage.removeItem(
            "chatMessages_" + sessionStorage.getItem("chatSessionId"));
          sessionStorage.removeItem("chatSessionId");
          if (!sessionStorage.getItem("chatSessionId")) {
            sessionStorage.setItem("chatSessionId", crypto.randomUUID());
          }
        }
        const roles = this.storageService.getItem("AspNetRole") ?? "";
        this.isDoctorOrAdmin = ["doctor", "admin"].some(r => roles.toLowerCase().includes(r));
        this.IsAIChatEnable = this.storageService.getItem("AIChatEnable");
      });

    const data = this.authService.getWebScoketData();
    if (data) {
      this.wsService.connect(data.userId, data.companyId);
      this.wsService.onForceLogout((reason) => {
        this.commonComponentService.startLogout(reason);
      });
    }
    const cacheBuster = `?v=${new Date().getTime()}`;
    let manifestFile = "manifest-physio.webmanifest";
    if (this.domain.includes("plus-doctor.com") || environment.environmentType === 'plusdoctor') {
      manifestFile = "manifest.webmanifest";
    }
    else if (this.domain.includes("plusradiology.ai") || environment.environmentType === 'plusradiology') {
      manifestFile = "manifest-radiology.webmanifest";
    }

    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      existingManifest.remove();
    }

    const manifestLink = document.createElement("link");
    manifestLink.setAttribute("rel", "manifest");
    manifestLink.setAttribute("href", `${manifestFile}${cacheBuster}`);
    document.head.appendChild(manifestLink);

    let faviconPath: string;
    if (this.domain.includes("plus-doctor.com") || environment.environmentType === 'plusdoctor') {
      faviconPath = "./assets/media/logos/plusdoctorfavicon.ico";
    } else if (this.domain.includes("plusradiology.ai") || environment.environmentType === 'plusradiology') {
      faviconPath = "./assets/media/logos/radiology-favicon.ico";
    } else {
      faviconPath = "./assets/media/logos/faviconn.ico";
    }
    this.setFavicon(faviconPath);

    if (this.domain.includes("plus-doctor.com") || environment.environmentType === 'plusdoctor') {
      this.titleService.setTitle("Plus Doctor");
    } else if (this.domain.includes("soundlife")) {
      this.titleService.setTitle("Soundlife");
    } else if (this.domain.includes("plusradiology.ai") || environment.environmentType === 'plusradiology') {
      this.titleService.setTitle("Plus Radiology");
    } else {
      this.titleService.setTitle("Plus Physio");
    }
    // enable/disable loader
    // this.loader = this.layoutConfigService.getConfig('loader.enabled');
    const routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // hide splash screen
        this.splashScreenService.hide();

        // scroll to top on every route change
        window.scrollTo(0, 0);
      }
    });
    this.unsubscribe.push(routerSubscription);

    // Apointment Toaster for Doctor And Receptionist
    this.userRoleService.userRole$.subscribe((newUserRole) => {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const currentRoute = event.url;
          if (currentRoute === "/" || currentRoute.includes("/display")) {
            // Route is "/", stop the schedule reminders
            this.toastrService.toastr.clear();
            this.toastrService.stopScheduleReminders();
          } else {
            if (
              newUserRole &&
              (newUserRole.includes("Doctor") || newUserRole.includes("Admin"))
            ) {
              // Route is not "/", start the schedule reminders
              this.toastrService.startScheduleReminders();
            }
          }
        }
      });
    });
  }

  applyInsets(insets: any) {
    document.documentElement.style.setProperty(
      '--safe-area-inset-top',
      `${insets.top}px`
    );

    document.documentElement.style.setProperty(
      '--safe-area-inset-bottom',
      `${insets.bottom}px`
    );
  }

  checkAndOpenTermsDialog(): void {
    const termInfo = this.shareService.getTermsInfo();
    const isAuthenticated = this.authService.isAuthenticated();

    if (!termInfo || !isAuthenticated) return;

    // Prevent multiple instances of the terms dialog
    const isAlreadyOpen = this.dialog.openDialogs.some(dialog =>
      dialog.componentInstance instanceof CommonComponentComponent &&
      (dialog.componentInstance as any).isTermandCondition
    );
    if (isAlreadyOpen) return;

    const isSkipped = this.storageService.getItem("isSkippedTerms");
    if (isSkipped != "true" && !termInfo.AgreeTerms) {
      const dialogRef = this.dialog.open(CommonComponentComponent, {
        disableClose: true,
        width: "600px",
        data: {
          termAndCondition: true,
        },
      });

      dialogRef.afterClosed().subscribe((accepted: boolean) => {
        if (accepted) {
          this.handleTermsAccepted();
          return;
        }
        this.handleTermsRejected();
      });
    } else {
      this.LoadTour();
    }
  }

  private handleTermsAccepted(): void {
    this.shareService.setAgreeTerms(true);
    this.LoadTour();
  }

  private handleTermsRejected(): void {
    const termInfo = this.shareService.getTermsInfo();
    if (termInfo?.SkipTermsCount > 2) {
      this.checkAndOpenTermsDialog();
    } else {
      this.LoadTour();
    }
  }

  LoadTour() {
    let IsTour = this.storageService.getItem("IsTour");
    if (IsTour == "false") {
      setTimeout(() => {
        this.initializeIntroduction();
      }, 1000);
    }
  }

  initializeIntroduction() {
    let hasCompleted = false;
    const menuList = Array.from(
      document.querySelectorAll(".kt-menu__link-text")
    );

    const newMenuList: HTMLElement[] = [];

    for (const element of menuList) {
      const menuElement = element as HTMLElement;
      newMenuList.push(menuElement);

      if (
        menuElement.textContent?.trim() === this.translate.instant("Setup i,+")
      ) {
        break;
      }
    }

    const menuSteps = [
      {
        key: "Dashboard",
        title: "Dashboard",
        intro:
          "You will get an idea about scheduled appointments, counts, appointments history provides you record of last appointments, to-do tasks list and billing details about payment and about staff leaves.",
        elementClass: ".clsDashboard",
      },
      {
        key: "Appointments",
        title: "Appointments",
        intro:
          "You can check the Doctor wise Patient's Appointments. It's provide you the functionality of create new appointment by selecting the doctor. You can edit an existing appointment by changing the date, time, or other details. You can Delete an existing appointment. You can access patient information, including medical history and insurance details.",
        elementClass: ".clsAppointments",
      },
      {
        key: "Patients",
        title: "Patients",
        intro:
          "Its provides you the functionality to manage and track patient information and medical history. You can access a list of all patients in the system, along with their basic information such as name, contact details and any relevant health information. Also you can add notes and comments to a patient's.",
        elementClass: ".clsPatients",
      },
      {
        key: "Invoices",
        title: "Billing",
        intro:
          "Its provides you the ability to manage and track billing and payment information for health care services provided to patients. You can create an bill for services provided to a patient, including details such as the service type, date of service, and payment amount. You can edit an existing bill if there are any changes to the service provided or payment amount.",
        elementClass: ".clsInvoices",
      },
      {
        key: "Reports",
        title: "Reports",
        intro:
          "You can generate different types of reports based on their needs, such as Bill reports, doctor wise appointment reports, also you have the ability to check recent patients Report. You can export and share reports with others in formats, such as Excel.",
        elementClass: ".clsReports",
      },
      {
        key: "WhatsApp",
        title: "WhatsApp",
        intro:
          "Integrate with WhatsApp via QR code to set up your business account. Simplify clinic message management and streamline bill generation",
        elementClass: ".clsWhatsApp",
      },
      {
        key: "Subscription Plan",
        title: "Subscription Plan",
        intro:
          "You can check the Subscription Plan for this Website. It's provide you the functionality of subscribe through various plans and also this provide information about plan and their respective facilities details.",
        elementClass: ".clsSubscription",
      },
      {
        key: "Setup ",
        title: "Setup",
        intro:
          "Its provides you the ability to configure and manage settings and options related to the application with sub menu's, user allows administrators to create and manage different user roles. General settings allows users to configure and manage general settings for the application and to-do allows users to create and manage to-do lists and more...",
        elementClass: ".clsSetup",
      },
    ];

    const steps = menuSteps
      .filter(({ key }) =>
        newMenuList.some(
          (x) => x.textContent?.trim() === this.translate.instant(key)
        )
      )
      .map(({ title, intro, elementClass }) => ({
        title,
        intro,
        element: document.querySelector(elementClass),
      }));

    this.intro.setOptions({
      steps: steps,
      showProgress: true,
      disableInteraction: true,
      exitOnOverlayClick: false,
    });

    this.intro.start();
    this.intro.onchange((targetElement) => {
      if (targetElement instanceof HTMLElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      this.intro.refresh();
    });

    this.intro.onbeforeexit(() => {
      if (!hasCompleted) {
        const companyId = this.storageService.getItem("companyId");
        this.dashboardService.setTourFalg(companyId).subscribe((res: any) => {
          if (res?.Success) {
            this.storageService.setItem("IsTour", "true");
            this.router.navigate(["/Dashboard"]);
          }
        });
      }
      return true;
    });

    this.intro.oncomplete(() => {
      hasCompleted = true;
      this.loaderService.show();
      const companyId = this.storageService.getItem("companyId");
      this.dashboardService.setTourFalg(companyId).subscribe((res: any) => {
        if (res?.Success) {
          this.storageService.setItem("IsTour", "true");
          this.loaderService.hide();
          this.router.navigate(["/Dashboard"]);
        } else {
          this.loaderService.hide();
        }
      });
    });
  }

  setFavicon(faviconUrl: string): void {
    let link: HTMLLinkElement | null =
      document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }
    link.href = faviconUrl;
  }

  /**
   * On Destroy
   */
  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  onExtend() {
    this.sessionTimer.extendSession();
    this.showWarningPopup = false;
  }

  onClose() {
    this.showWarningPopup = false;
  }

  ngAfterViewInit(): void {
    this.commonComponentService.resumeIfNeeded();
  }

  OpenAIChatBot() {
    const sessionKey = "chatSessionId";
    const sessionDateKey = "chatSessionDate";

    // Get stored values
    let sessionId = sessionStorage.getItem(sessionKey);
    let sessionDate = sessionStorage.getItem(sessionDateKey);

    // Get today's date (YYYY-MM-DD)
    const today = new Date().toISOString().split("T")[0];

    // If first run OR the date changed → clear session
    if (!sessionId || sessionDate !== today) {
      sessionStorage.clear(); // clear all previous chat data
      sessionId = crypto.randomUUID();
      sessionStorage.setItem(sessionKey, sessionId);
      sessionStorage.setItem(sessionDateKey, today);
    }

    const dialogRef = this.dialog.open(ChatbotComponent, {
      panelClass: "chatbot-dialog",
      position: { right: "0" },
      height: "100vh",
      width: "400px",
      disableClose: false,
      // data: {
      //   sessionId: sessionId,
      //   username: 'User' // optional
      // }
    });

    dialogRef.componentInstance.chatUpdated.subscribe(
      (update: { sessionId: string; messages: any[] }) => {
        sessionStorage.setItem(
          "chatMessages_" + update.sessionId,
          JSON.stringify(update.messages)
        );
      }
    );
  }

  checkDomain() {
    const hostname = ((typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();
    const map = [
      { test: /(^|\.)plus-doctor\.com$/.test(hostname) || environment.environmentType === 'plusdoctor', type: DomainType.Doctor, name: '+Doctor', title: 'Plus Doctor', manifest: 'manifest.webmanifest', favicon: './assets/media/logos/plusdoctorfavicon.ico' },
      { test: /(^|\.)plusradiology\.ai$/.test(hostname) || environment.environmentType === 'plusradiology', type: DomainType.Radiology, name: '+Radiology', title: 'Plus Radiology', manifest: 'manifest-radiology.webmanifest', favicon: './assets/media/logos/radiology-favicon.ico' },
    ];
    const entry = map.find(m => m.test);
    if (entry) {
      this.domain = hostname;
      this.shareService.domainType = entry.type;
      this.shareService.domainName = entry.name;
      return entry;
    }
    // default physio
    this.domain = hostname;
    this.shareService.domainType = DomainType.Physio;
    this.shareService.domainName = '+PHYSIO';
    this.shareService.domainNameString = 'PlusPhysio';
    this.shareService.domain = globalThis.location.host
    if (this.shareService.domain.includes("plus-doctor.com") || this.shareService.domain.includes("soundlife") || environment.environmentType === 'plusdoctor') {
      this.shareService.domainName = "+Doctor";
      this.shareService.domainNameString = "PlusDoctor";
      this.shareService.domainType = DomainType.Doctor;
      this.shareService.plusDoctor = true;
      this.shareService.domainType = DomainType.Doctor;
    } else if (this.shareService.domain.includes("plusradiology.ai") || environment.environmentType === 'plusradiology') {
      this.shareService.domainName = "+Radiology";
      this.shareService.domainNameString = "PlusRadiology";
      this.shareService.domainType = DomainType.Radiology;
      this.shareService.plusDoctor = false;
      this.shareService.domainType = DomainType.Radiology;
    }
    else {
      this.shareService.domainName = "+PHYSIO";
      this.shareService.domainType = DomainType.Physio;
      this.shareService.plusDoctor = false;
    }
  }
}
